// Import necessary React hooks
import { useEffect, useState } from "react";
// Import necessary Material-UI components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
// Import custom validation function, base64 utilities and hooks
import { validateItemInput } from "../../validation/validateItemInput.js";
import { useResubmitRequestContext, useResubmitRequest } from "./";
import {
  CustomBackdrop,
  ItemDetailsFormCommon,
  ViewImage,
  processImages,
} from "../../../common";

export function ResubmitEditDeleteItemSampleRequestModal(props) {
  // Access the current state of the new request
  const { state } = useResubmitRequestContext();
  // Hook to set sample request details
  const { setSampleRequestDetails } = useResubmitRequest();

  // State to store new product images to be uploaded
  const [productImagesNew, setProductImagesNew] = useState([]);
  // State to store existing product images
  const [existingProductImages, setExistingProductImages] = useState(
    // If props.data.productImage is an array, use it directly
    (Array.isArray(props.data.productImage) && props.data.productImage) ||
      // If props.data.productImage has a name property, wrap it in an array
      (props.data.productImage.name ? [props.data.productImage] : [])
  );
  // State for storing boolean for progress of image compression
  const [isCompressing, setIsCompressing] = useState(false);
  // State for storing timer for Secondary Message in CustomBackdrop
  const [patienceTimer, setPatienceTimer] = useState(false);
  // State to store any validation errors
  const [errors, setErrors] = useState();

  // State for storing form input values
  const [values, setValues] = useState({
    // Initial values for form fields
    sampleItemID: "",
    itemERPCode: "",
    itemDescription: "",
    specification: "",
    noOfSamplesRequired: "",
    productClass: "",
    productImage: [],
  });

  // Effect to initialize form values when the modal opens based on props.data
  useEffect(() => {
    setValues({
      sampleItemID: props.data.sampleItemID,
      itemERPCode: props.data.itemERPCode,
      itemDescription: props.data.itemDescription,
      specification: props.data.specification,
      quantityForSample: props.data.quantityForSample,
      preferredUnitOfMeasure: props.data.preferredUnitOfMeasure,
      noOfSamplesRequired: props.data.noOfSamplesRequired,
      productClass: props.data.productClass,
      productImage:
        // If props.data.productImage is an array, use it directly
        (Array.isArray(props.data.productImage) && props.data.productImage) ||
        // If props.data.productImage has a name property, wrap it in an array
        (props.data.productImage.name ? [props.data.productImage] : []),
    });
    setExistingProductImages(
      // If props.data.productImage is an array, use it directly
      (Array.isArray(props.data.productImage) && props.data.productImage) ||
        // If props.data.productImage has a name property, wrap it in an array
        (props.data.productImage.name ? [props.data.productImage] : [])
    );
  }, [props.data]);

  // Function to close the modal and reset form states
  const handleClose = () => {
    props.setOpen(false);
    setValues({
      sampleItemID: "",
      itemERPCode: "",
      itemDescription: "",
      specification: "",
      noOfSamplesRequired: "",
      productClass: "",
      productImage: [],
    });
    setProductImagesNew([]);
    setExistingProductImages([]);
  };

  // Handler for form input changes
  const onChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  // Handler to remove an existing product image
  const handleImageRemove = (index) => {
    const updatedImages = [...existingProductImages];
    updatedImages.splice(index, 1);
    setExistingProductImages(updatedImages);
  };

  // Function to handle form submission for editing an item
  const handleSetData = async (data) => {
    setErrors({});
    const newArr = state.sampleRequestDetails.items.filter(
      (obj) => obj.sampleItemID !== state.itemFormToEdit.sampleItemID
    );

    // Process all of the images primed for upload - compress images and then convert to base64
    let productImagesToUpload = await processImages(
      productImagesNew,
      setPatienceTimer,
      setIsCompressing
    );

    // Logic to update the sample request details with the edited item
    setSampleRequestDetails({
      ...state.sampleRequestDetails,
      items: [
        {
          ...values,
          productImage: [...existingProductImages, ...productImagesToUpload],
        },
        ...newArr,
      ],
    });
    // Logic to Reset form states
    setValues({
      sampleItemID: "",
      itemERPCode: "",
      itemDescription: "",
      specification: "",
      quantityForSample: "",
      preferredUnitOfMeasure: "",
      noOfSamplesRequired: "",
      productClass: "",
      productImage: [],
    });
    setProductImagesNew([]);
    setExistingProductImages([]);
    handleClose();
  };

  // Function to handle form submission for deleting an item
  const handleDelete = (data) => {
    setErrors({});
    const newArr = state.sampleRequestDetails.items.filter(
      (obj) => obj.sampleItemID !== state.itemFormToEdit.sampleItemID
    );
    // Logic to update the sample request details with the edited item
    setSampleRequestDetails({
      ...state.sampleRequestDetails,
      items: [...newArr],
    });
    // Logic to Reset form states
    setValues({
      sampleItemID: "",
      itemERPCode: "",
      itemDescription: "",
      specification: "",
      quantityForSample: "",
      preferredUnitOfMeasure: "",
      noOfSamplesRequired: "",
      productClass: "",
      productImage: [],
    });
    handleClose();
  };

  // Function to handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    const { validationErrors, isValid } = validateItemInput(values);
    !isValid && setErrors(validationErrors);
    isValid && handleSetData(values);
  };
  errors && console.log({ errors });

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={handleClose}
        style={{
          height: "90%",
        }}
        PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <Box
          sx={{
            display: props.display,
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Container
            component="main"
            maxWidth="sm"
            sx={{
              marginTop: 4,
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography component="h1" variant="h5">
              Edit Item
            </Typography>
            <CssBaseline />
            <Box
              component="form"
              noValidate
              sx={{
                mt: 5,
              }}
            >
              <ItemDetailsFormCommon
                values={values}
                onChange={onChange}
                errors={errors}
                productImage={productImagesNew}
                setProductImage={setProductImagesNew}
              />
              {/* Display the product images present in the item details before editing§ */}

              {Array.isArray(existingProductImages) &&
                existingProductImages.map((imageObject, index) => (
                  <Grid container key={index}>
                    <Grid item xs={12}>
                      {imageObject.sampleImageURL && (
                        <ViewImage productImage={imageObject} />
                      )}
                      {imageObject.image && (
                        <img
                          src={imageObject.image}
                          alt={` ${index}`}
                          style={{ width: "100px" }}
                        />
                      )}
                    </Grid>
                    <Button
                      sx={{
                        mt: 3,
                        mb: 3,
                      }}
                      color="error"
                      onClick={() => handleImageRemove(index)}
                    >
                      Remove
                    </Button>
                  </Grid>
                ))}
              <Grid
                container
                spacing={2}
                sx={{
                  mt: 1,

                  flexDirection: "row",
                  justifyContent: "center",
                }}
              >
                <Grid item>
                  <Button
                    onClick={handleDelete}
                    variant="contained"
                    color="error"
                    sx={{
                      mt: 3,
                      mb: 3,
                      width: "150px",
                    }}
                  >
                    Delete
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                      mt: 3,
                      mb: 3,
                      width: "150px",
                    }}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>
      </Dialog>
      <CustomBackdrop
        open={isCompressing}
        primaryMessage="Please wait: Compressing selected images"
        timer={patienceTimer}
        secondaryMessage="Larger images take longer to compress."
      />
    </div>
  );
}
